<template>
    <div v-if="'cards' in data" class="cards">
        <div v-for="(card, i) in data.cards" :key="i" class="card-item">
            <a-product-card class="card" v-bind="cardProps(card)">
                {{ card.text }}
            </a-product-card>
        </div>
    </div>
    <div v-else>
        <a-product-card class="card single" v-bind="cardProps(data)">
            {{ data.text }}
        </a-product-card>
    </div>
</template>

<script setup lang="ts">
import AProductCard from '@core/components/product-card/product-card.vue';
import type { BodyVariant, CtaProductCard, CtaProductsData } from '@model/editor-js/blocks';

defineOptions({
    name: 'BlockCtaProducts',
});

defineProps<{
    id?: string;
    /** `ctaoneproducttool` stores a single card, the two/three tools store `cards` */
    data: CtaProductCard | CtaProductsData;
    variant: BodyVariant;
}>();

// links without both a label and a target are not rendered as buttons
function cardProps(card: CtaProductCard): Record<string, unknown> {
    const { learnLink, tryLink } = card.links;
    return {
        ...card,
        ...(learnLink.text && learnLink.to ? { learnLink } : {}),
        ...(tryLink.text && tryLink.to ? { tryLink } : {}),
    };
}
</script>

<style lang="pcss" scoped>
/* blog layout; TRU and threat catalog have no live CTA content and share it. Resource bodies (RC & PR) keep plain cards */
:is(.variant-blog, .variant-tru) {
    .cards {
        margin: 40px 0;
        display: grid;
        column-gap: 16px;
        row-gap: 16px;

        @media (--viewport-tablet) {
            grid-template-columns: 1fr 1fr;
        }
    }

    @media (--viewport-tablet) {
        .card-item:deep(.a-product-card__footer) {
            gap: 16px;
            flex-direction: row;
            flex-wrap: nowrap;
            margin: 20px 0 0 0;

            .a-product-card__button {
                flex: 1;
                margin: 0;
            }
        }
    }

    @media all and (max-width: 768px) {
        .card:deep(.a-product-card__footer) {
            flex-direction: column-reverse;
            justify-content: end;
            flex-grow: 1;

            .a-product-card__button {
                flex: unset;
            }
        }
    }

    @media (--viewport-tablet) {
        .card.single {
            display: flex;
            flex-direction: row;

            :deep(> div:first-of-type) {
                margin-inline-end: auto;
            }

            :deep(.a-product-card__footer) {
                margin: 0 0 0 32px;
                gap: 16px;
                height: fit-content;
                max-width: 181px;
            }

            :deep(.a-product-card__button) {
                min-width: 181px;
                width: 100%;
                height: fit-content;
                margin-inline-start: 0;
                margin-inline-end: 0;
            }
        }
    }
}
</style>
